(() => {
  const drawer = document.getElementById('ElementCartDrawer');
  if (!drawer || drawer.dataset.bound === 'true') return;
  drawer.dataset.bound = 'true';

  const panel = drawer.querySelector('.el-cart-drawer__panel');
  const body = drawer.querySelector('[data-el-cart-body]');
  const footer = drawer.querySelector('[data-el-cart-footer]');
  const subtotal = drawer.querySelector('[data-el-cart-subtotal]');
  const productsTotal = drawer.querySelector('[data-el-cart-products]');
  const discountRow = drawer.querySelector('[data-el-cart-discount-row]');
  const discountTotal = drawer.querySelector('[data-el-cart-discount]');
  const countLabels = drawer.querySelectorAll('[data-el-cart-count]');
  const status = drawer.querySelector('[data-el-cart-status]');
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  let lastFocused = null;
  let busy = false;

  const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', "'": '&#39;', '"': '&quot;'
  })[character]);

  const formatMoney = (cents, currency = 'USD') => {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency', currency, minimumFractionDigits: 2
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `$${(Number(cents || 0) / 100).toFixed(2)}`;
    }
  };

  const cartEndpoint = (path) => `${root}cart/${path}.js`;

  async function requestCart(path = '') {
    const response = await fetch(path ? cartEndpoint(path) : `${root}cart.js`, {
      headers: { Accept: 'application/json' }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || 'The cart could not be updated.');
    return data;
  }

  function publicProperties(properties) {
    if (!properties) return '';
    return Object.entries(properties)
      .filter(([key, value]) => key.charAt(0) !== '_' && value)
      .map(([key, value]) => `<p class="el-cart-drawer__property">${escapeHtml(key)}: ${escapeHtml(value)}</p>`)
      .join('');
  }

  function render(cart) {
    const currency = cart.currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    countLabels.forEach((label) => { label.textContent = `(${cart.item_count})`; });
    updateHeaderCount(cart.item_count);
    const visibleProductsTotal = (cart.items || []).reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
    const additionalDiscount = Math.max(0, visibleProductsTotal - Number(cart.total_price || 0));
    productsTotal.textContent = formatMoney(visibleProductsTotal, currency);
    subtotal.textContent = formatMoney(cart.total_price, currency);
    discountRow.hidden = additionalDiscount < 1;
    discountTotal.textContent = additionalDiscount > 0 ? `−${formatMoney(additionalDiscount, currency)}` : '';

    if (!cart.items || cart.items.length === 0) {
      body.innerHTML = '<div class="el-cart-drawer__empty"><div class="el-cart-drawer__empty-inner"><div class="el-cart-drawer__empty-icon" aria-hidden="true">↗</div><h3>Your cart is ready for a routine.</h3><p>Add a product and it will appear here without leaving the page.</p></div></div>';
      footer.hidden = true;
      return;
    }

    body.innerHTML = cart.items.map((item, index) => {
      const image = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
      const variant = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="el-cart-drawer__variant">${escapeHtml(item.variant_title)}</p>` : '';
      const plan = item.selling_plan_allocation && item.selling_plan_allocation.selling_plan
        ? `<p class="el-cart-drawer__plan">${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</p>` : '';
      return `<article class="el-cart-drawer__item" data-cart-line="${index + 1}">
        <div class="el-cart-drawer__image">${image ? `<img src="${escapeHtml(image)}" alt="" width="184" height="216" loading="lazy">` : ''}</div>
        <div class="el-cart-drawer__item-copy">
          <div class="el-cart-drawer__item-top"><h3 class="el-cart-drawer__item-title">${escapeHtml(item.product_title || item.title)}</h3><span class="el-cart-drawer__item-price">${formatMoney(item.final_line_price, currency)}</span></div>
          ${variant}${plan}${publicProperties(item.properties)}
          <div class="el-cart-drawer__controls">
            <div class="el-cart-drawer__quantity" aria-label="Quantity for ${escapeHtml(item.product_title || item.title)}">
              <button type="button" data-cart-quantity="${item.quantity - 1}" data-cart-line-index="${index + 1}" aria-label="Decrease quantity">−</button>
              <span aria-live="polite">${item.quantity}</span>
              <button type="button" data-cart-quantity="${item.quantity + 1}" data-cart-line-index="${index + 1}" aria-label="Increase quantity">+</button>
            </div>
            <button class="el-cart-drawer__remove" type="button" data-cart-quantity="0" data-cart-line-index="${index + 1}">Remove</button>
          </div>
        </div>
      </article>`;
    }).join('');
    footer.hidden = false;
  }

  function updateHeaderCount(itemCount) {
    const cartLink = document.getElementById('cart-icon-bubble');
    if (!cartLink) return;
    let bubble = cartLink.querySelector('.cart-count-bubble');
    if (itemCount < 1) {
      if (bubble) bubble.remove();
      return;
    }
    if (!bubble) {
      bubble = document.createElement('div');
      bubble.className = 'cart-count-bubble';
      bubble.innerHTML = '<span aria-hidden="true"></span><span class="visually-hidden"></span>';
      cartLink.appendChild(bubble);
    }
    bubble.querySelector('[aria-hidden="true"]').textContent = itemCount;
    bubble.querySelector('.visually-hidden').textContent = `${itemCount} items in cart`;
  }

  async function refresh(message = '') {
    body.setAttribute('aria-busy', 'true');
    if (message) status.textContent = message;
    try {
      const cart = await requestCart();
      render(cart);
      return cart;
    } catch (error) {
      status.textContent = error.message;
      throw error;
    } finally {
      body.removeAttribute('aria-busy');
    }
  }

  async function open(options = {}) {
    lastFocused = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('el-cart-open');
    status.textContent = options.message || '';
    await refresh(options.message || '');
    panel.focus({ preventScroll: true });
  }

  function close() {
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('el-cart-open');
    status.textContent = '';
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus({ preventScroll: true });
  }

  async function change(line, quantity) {
    if (busy) return;
    busy = true;
    body.setAttribute('aria-busy', 'true');
    try {
      const response = await fetch(cartEndpoint('change'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ line, quantity })
      });
      const cart = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(cart.description || 'The quantity could not be updated.');
      render(cart);
      status.textContent = quantity === 0 ? 'Item removed.' : 'Quantity updated.';
    } catch (error) {
      status.textContent = error.message;
    } finally {
      busy = false;
      body.removeAttribute('aria-busy');
    }
  }

  drawer.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-el-cart-close]');
    if (closeButton) return close();
    const quantityButton = event.target.closest('[data-cart-line-index]');
    if (quantityButton) change(Number(quantityButton.dataset.cartLineIndex), Number(quantityButton.dataset.cartQuantity));
  });

  document.addEventListener('keydown', (event) => {
    if (!drawer.classList.contains('is-open')) return;
    if (event.key === 'Escape') close();
    if (event.key !== 'Tab') return;
    const focusable = [...drawer.querySelectorAll('button:not([disabled]), a[href]')].filter((element) => element.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
    if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
  });

  const cartLink = document.getElementById('cart-icon-bubble');
  if (cartLink) cartLink.addEventListener('click', (event) => { event.preventDefault(); open(); });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action*="/cart/add"]');
    if (!form || form.matches('.modal-form, #eo-add-to-cart-form, [data-element-cart-managed]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submit = event.submitter || form.querySelector('[type="submit"]');
    const original = submit ? submit.innerHTML : '';
    if (submit) { submit.disabled = true; submit.textContent = 'Adding…'; }
    try {
      const response = await fetch(`${root}cart/add.js`, { method: 'POST', headers: { Accept: 'application/json' }, body: new FormData(form) });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.description || 'This item could not be added.');
      await open({ message: 'Added to your cart.' });
    } catch (error) {
      status.textContent = error.message;
      drawer.classList.add('is-open');
      drawer.setAttribute('aria-hidden', 'false');
      document.body.classList.add('el-cart-open');
    } finally {
      if (submit) { submit.disabled = false; submit.innerHTML = original; }
    }
  }, true);

  window.ElementCart = { open, close, refresh, change };
  window.openCartDrawer = open;
})();
